import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { 
  WaterCapacitiesRequest, 
  WaterCapacitiesResponse,
  WaterCapacitiesDetailRequest,
  WaterCapacitiesDetailResponse,
  WaterCapacities,
  SaveWaterCapacitiesRequest,
  DeleteWaterCapacitiesRequest,
  WaterCapacitiesRow,
  DropdownColumn,
  WaterCapacitiesCascadingFilterResponse,
  FilterColumn
} from '../services/interfaces/water-capacities-view.interfaces';
import { ErrorHandler } from '../utils/error-handler';
import { environment } from '../environments/environments';

@Injectable({
  providedIn: 'root',
})
export class WaterCapacitiesViewService {
  private baseUrl = environment.WebApiURL;
  private readonly waterCapacitiesExportTabName = 'WaterCapacity';

  // TODO: Update these endpoints when API is ready
  private dropdownUrl(): string {
    return `${this.baseUrl}/api/WaterCapacities/GetWaterCapacitiesDropDown`;
  }

  private filterOptionsUrl(): string {
    return `${this.baseUrl}/api/WaterCapacities/GetWaterCapacitiesFilters`;
  }

  private detailsUrl(): string {
    return `${this.baseUrl}/api/WaterCapacities/GetWaterCapacityDetails`;
  }

  private saveUrl(): string {
    return `${this.baseUrl}/api/WaterCapacities/InsertUpdateWaterCapacity`;
  }

  private deleteUrl(): string {
    return `${this.baseUrl}/api/WaterCapacities/RemoveWaterCapacity`;
  }

  private cascadingFilterUrl(): string {
    return `${this.baseUrl}/GetWaterCapacitiesCascadingFilter`;
  }

  private exportUrl(): string {
    return `${this.baseUrl}/api/FlowNetworkFE/ExportAllTabsData`;
  }

  constructor(private http: HttpClient) {}

  async exportAllWaterCapacitiesData(): Promise<{ blob: Blob; fileName: string }> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const httpResponse = await firstValueFrom(
      this.http.post(this.exportUrl(), { tabName: this.waterCapacitiesExportTabName }, {
        headers,
        observe: 'response' as const,
        responseType: 'blob' as const,
      })
    );

    const blob = httpResponse.body;
    if (!blob) {
      throw new Error('Export returned an empty file.');
    }

    const contentDisposition = httpResponse.headers.get('content-disposition') || '';
    const contentType = httpResponse.headers.get('content-type') || blob.type || '';
    const fileNameMatch = contentDisposition.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i);
    const rawHeaderFileName = fileNameMatch?.[1]?.trim() || '';
    const decodedHeaderFileName = decodeURIComponent(rawHeaderFileName.replace(/^"|"$/g, ''));
    const normalizedContentType = contentType.toLowerCase();
    const inferredExtension =
      normalizedContentType.includes('spreadsheetml') ||
      normalizedContentType.includes('xlsx') ||
      decodedHeaderFileName.toLowerCase().endsWith('.xlsx')
        ? '.xlsx'
        : '.csv';
    const fallbackFileName = `water-capacities-view${inferredExtension}`;
    const decodedFileName = rawHeaderFileName ? decodedHeaderFileName : fallbackFileName;
    const fileName = /\.[A-Za-z0-9]+$/.test(decodedFileName)
      ? decodedFileName
      : `${decodedFileName}${inferredExtension}`;

    return { blob, fileName };
  }

  private normalizeText(value: string | null | undefined): string {
    return (value ?? '').trim();
  }

  private normalizeDateForInput(value: string | null | undefined): string {
    const raw = this.normalizeText(value);
    if (!raw) {
      return '';
    }

    const datePart = raw.includes('T') ? raw.split('T')[0] : raw;
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      return datePart;
    }

    const slashDateMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slashDateMatch) {
      const [, month, day, year] = slashDateMatch;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    const parsed = new Date(raw);
    if (!isNaN(parsed.getTime())) {
      const year = parsed.getFullYear();
      const month = String(parsed.getMonth() + 1).padStart(2, '0');
      const day = String(parsed.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    return '';
  }

  private toNumberOrNull(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  private mapApiItemToWaterCapacity(item: any): WaterCapacities {
    return {
      devArea: this.normalizeText(item?.developmentArea),
      waterFacilityName: this.normalizeText(item?.facilityName ?? item?.surfaceFacilityName),
      facilityType: this.normalizeText(item?.facilityType),
      capacity: this.toNumberOrNull(item?.capacity ?? item?.waterDisposalCapacity),
      effectiveDate: this.normalizeDateForInput(item?.effectiveDate),
      comments: this.normalizeText(item?.comments),
      isActive: 'Y'
    };
  }

  private parseDropdownResponse(response: DropdownColumn[]): WaterCapacitiesResponse {
    const findColumn = (name: string) =>
      response.find(item => (item.columnName || '').toLowerCase() === name.toLowerCase());

    const devAreaItem = findColumn('DevelopmentArea') || findColumn('DevArea');
    const subDevAreaItem = findColumn('SubDevelopmentArea');
    const waterFacilityNameItem = findColumn('FacilityName') || findColumn('WaterFacilityName') || findColumn('SurfaceFacilityName');
    const facilityTypeItem = findColumn('FacilityType');

    const developmentAreas = Array.from(new Set(devAreaItem?.values || [])).filter(Boolean);
    const subDevelopmentAreas = Array.from(new Set(subDevAreaItem?.values || [])).filter(Boolean);
    const devAreas = Array.from(new Set([
      ...developmentAreas,
      ...subDevelopmentAreas,
    ])).filter(Boolean);

    return {
      developmentAreas,
      subDevelopmentAreas,
      devAreas,
      waterFacilityNames: waterFacilityNameItem?.values || [],
      facilityTypes: facilityTypeItem?.values || [],
    };
  }

  async getAllDropdownOptions(filters?: {
    developmentArea?: string | null;
    facilityName?: string | null;
    facilityType?: string | null;
  }): Promise<WaterCapacitiesResponse> {
    try {
      const requestBody = {
        developmentArea: filters?.developmentArea || null,
        facilityName: filters?.facilityName || null,
        facilityType: filters?.facilityType || null,
      };

      const response = await firstValueFrom(
        this.http.post<DropdownColumn[]>(this.dropdownUrl(), requestBody)
      );

      return this.parseDropdownResponse(response || []);
    } catch (error) {
      ErrorHandler.handleError(error, 'WaterCapacitiesViewService.getAllDropdownOptions');
      const errorMessage = ErrorHandler.extractApiErrorMessage(error);
      const customError = new Error(errorMessage);
      (customError as any).originalError = error;
      throw customError;
    }
  }

  async getFilterOptionsBySelection(filters?: {
    developmentArea?: string | null;
    facilityName?: string | null;
    facilityType?: string | null;
  }): Promise<WaterCapacitiesResponse> {
    try {
      const requestBody = {
        developmentArea: filters?.developmentArea || null,
        facilityName: filters?.facilityName || null,
        facilityType: filters?.facilityType || null,
      };

      const response = await firstValueFrom(
        this.http.post<DropdownColumn[]>(this.filterOptionsUrl(), requestBody)
      );

      return this.parseDropdownResponse(response || []);
    } catch (error) {
      ErrorHandler.handleError(error, 'WaterCapacitiesViewService.getFilterOptionsBySelection');
      const errorMessage = ErrorHandler.extractApiErrorMessage(error);
      const customError = new Error(errorMessage);
      (customError as any).originalError = error;
      throw customError;
    }
  }

  async getWaterCapacitiesData(filters?: Partial<WaterCapacitiesDetailRequest>): Promise<WaterCapacities[]> {
    try {
      const paged = await this.getWaterCapacitiesDataWithPagination(filters);
      return paged.data;
    } catch (error) {
      ErrorHandler.handleError(error, 'WaterCapacitiesViewService.getWaterCapacitiesData');
      console.error('Error fetching water capacities data:', error);
      return [];
    }
  }

  async getWaterCapacitiesDataWithPagination(filters?: Partial<WaterCapacitiesDetailRequest>): Promise<{
    data: WaterCapacities[];
    nextRowNumber: number | null;
  }> {
    try {
      const payload = {
        developmentArea: filters?.devArea || null,
        facilityName: filters?.waterFacilityName || null,
        facilityType: filters?.facilityType || null,
        startDate: filters?.startDate || null,
        endDate: filters?.endDate || null,
        lastRowNumber: filters?.lastRowNumber ?? 0
      };

      const response = await firstValueFrom(
        this.http.post<any>(this.detailsUrl(), payload)
      );

      const parsed = ErrorHandler.parseApiResult(response);
      if (parsed.success) {
        return {
          data: (response?.waterItems || []).map((item: any) => this.mapApiItemToWaterCapacity(item)),
          nextRowNumber: response?.nextRowNumber ?? null
        };
      }

      console.warn('API returned non-success status:', parsed.statusCode, parsed.message);
      return {
        data: [],
        nextRowNumber: null
      };
    } catch (error) {
      ErrorHandler.handleError(error, 'WaterCapacitiesViewService.getWaterCapacitiesDataWithPagination');
      console.error('Error fetching water capacities data:', error);
      return {
        data: [],
        nextRowNumber: null
      };
    }
  }

  async getCascadingFilterOptions(filters?: Partial<WaterCapacitiesRequest>): Promise<WaterCapacitiesCascadingFilterResponse> {
    try {
      // TODO: Remove static data and uncomment API call when ready
      return [];

      /* Uncomment when API is ready:
      const payload: WaterCapacitiesRequest = {
        devArea: filters?.devArea || null,
        waterFacilityName: filters?.waterFacilityName || null,
        capacity: filters?.capacity || null,
        startDate: filters?.startDate || null,
        endDate: filters?.endDate || null,
        comments: filters?.comments || null
      };

      const response = await firstValueFrom(
        this.http.post<WaterCapacitiesCascadingFilterResponse>(this.cascadingFilterUrl(), payload)
      );

      return response || [];
      */
    } catch (error) {
      ErrorHandler.handleError(error, 'WaterCapacitiesViewService.getCascadingFilterOptions');
      console.error('Error fetching cascading filter options:', error);
      return [];
    }
  }

  async saveWaterCapacities(row: WaterCapacitiesRow, action: 'insert' | 'update', userName: string): Promise<any> {
    try {
      const updatedEffectiveDate = row.effectiveDate || '';
      const body: SaveWaterCapacitiesRequest = {
        action: action,
        developmentAreas: row.devArea || '',
        facilityType: row.facilityType || '',
        facilityName: row.waterFacilityName || '',
        capacity: row.capacity,
        newEffectiveDate: updatedEffectiveDate,
        oldEffectiveDate: action === 'update' && row.original?.effectiveDate ? row.original.effectiveDate : '',
        comment: row.comments || '',
        userName: userName
      };

      const response = await firstValueFrom(
        this.http.post<any>(this.saveUrl(), body)
      );

      const parsed = ErrorHandler.parseApiResult(response);
      return {
        statusCode: parsed.statusCode,
        statusMessage: parsed.message,
        error: !parsed.success,
        ...response,
      };
    } catch (error: any) {
      console.error('Error saving water capacities:', error);
      const parsed = ErrorHandler.parseApiResult(error?.error || error);
      return {
        statusCode: parsed.statusCode || error?.status || 500,
        statusMessage: parsed.message,
        error: true,
      };
    }
  }

  async saveMultipleWaterCapacities(rows: WaterCapacitiesRow[], userName: string): Promise<any[]> {
    const results: any[] = [];
    
    for (const row of rows) {
      const action: 'insert' | 'update' = row.isNew ? 'insert' : 'update';
      
      const result = await this.saveWaterCapacities(row, action, userName);
      results.push(result);
    }

    return results;
  }

  async deleteWaterCapacities(row: WaterCapacitiesRow, userName: string): Promise<any> {
    if (!row.devArea || !row.waterFacilityName || !row.facilityType || !row.effectiveDate || !userName) {
      return {
        statusCode: 400,
        statusMessage: 'developmentAreas, facilityName, facilityType, effectiveDate and userName are required for deletion.',
        error: true,
      };
    }

    try {
      const body: DeleteWaterCapacitiesRequest = {
        developmentAreas: row.devArea,
        facilityName: row.waterFacilityName,
        facilityType: row.facilityType,
        effectiveDate: row.effectiveDate,
        userName
      };

      const response = await firstValueFrom(
        this.http.post<any>(this.deleteUrl(), body)
      );

      if (typeof response === 'string') {
        return {
          statusCode: 200,
          statusMessage: response,
          error: false,
        };
      }

      const parsed = ErrorHandler.parseApiResult(response);
      return {
        statusCode: parsed.statusCode,
        statusMessage: parsed.message,
        error: !parsed.success,
        ...response,
      };
    } catch (error: any) {
      console.error('Error deleting water capacities:', error);
      const parsed = ErrorHandler.parseApiResult(error?.error || error);
      return {
        statusCode: parsed.statusCode || error?.status || 500,
        statusMessage: parsed.message,
        error: true,
      };
    }
  }

  async deleteMultipleWaterCapacities(rows: WaterCapacitiesRow[], userName: string): Promise<any[]> {
    const results: any[] = [];
    
    for (const row of rows) {
      if (row.isNew) {
        continue;
      }
      
      const result = await this.deleteWaterCapacities(row, userName);
      results.push(result);
    }

    return results;
  }
}

export type { 
  WaterCapacitiesRequest,
  WaterCapacitiesResponse,
  WaterCapacitiesDetailRequest,
  WaterCapacitiesDetailResponse,
  WaterCapacities,
  SaveWaterCapacitiesRequest,
  DeleteWaterCapacitiesRequest,
  WaterCapacitiesRow,
  DropdownColumn,
  WaterCapacitiesCascadingFilterResponse,
  FilterColumn
} from '../services/interfaces/water-capacities-view.interfaces';